import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Icon, { type IconName } from "@/components/Icon";
import Skeleton from "@/components/Skeleton";
import { listMyCourses } from "@/api/course";
import type { Course } from "@/types";

/** Aperçu de l'espace enseignant : quelques indicateurs dérivés de la liste des cours
 *  (aucun endpoint dédié) + accès rapide. Volontairement léger pour un premier jet. */
export default function InstructorDashboard() {
  const [courses, setCourses] = useState<Course[] | null>(null);
  useEffect(() => { listMyCourses().then(setCourses).catch(() => setCourses([])); }, []);

  const kpis = useMemo(() => {
    const cs = courses ?? [];
    const active = cs.filter((c) => !c.archived);
    const classes = new Set<string>();
    cs.forEach((c) => c.class_names.forEach((n) => classes.add(n)));
    return {
      courses: active.length,
      students: active.reduce((sum, c) => sum + c.student_count, 0),
      classes: classes.size,
    };
  }, [courses]);

  return (
    <div>
      <p style={styles.crumb}>Espace enseignant</p>
      <h1 style={styles.title}>Tableau de bord</h1>

      {courses === null ? (
        <div style={styles.grid}>{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} height={96} />)}</div>
      ) : (
        <div style={styles.grid}>
          <Kpi icon="checklist" label="Cours actifs" value={kpis.courses} />
          <Kpi icon="home" label="Élèves inscrits" value={kpis.students} />
          <Kpi icon="panel" label="Classes" value={kpis.classes} />
        </div>
      )}

      <div style={styles.actions}>
        <Link to="/instructor/courses" style={styles.action}>
          <Icon name="next" size={16} /> Voir mes cours
        </Link>
        <Link to="/instructor/archives" style={styles.action}>
          <Icon name="next" size={16} /> Cours archivés
        </Link>
      </div>
    </div>
  );
}

function Kpi({ icon, label, value }: { icon: IconName; label: string; value: number }) {
  return (
    <div style={styles.kpi}>
      <span style={styles.kpiIcon}><Icon name={icon} size={20} /></span>
      <div>
        <div style={styles.kpiValue}>{value}</div>
        <div style={styles.kpiLabel}>{label}</div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  crumb: { margin: 0, fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1 },
  title: { margin: "4px 0 24px", fontSize: 26, fontWeight: 700, color: "var(--text)" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, maxWidth: 720 },
  kpi: { display: "flex", alignItems: "center", gap: 14, background: "var(--bg-panel)", border: "1px solid var(--border-soft)", borderRadius: "var(--radius-lg)", padding: "18px 20px" },
  kpiIcon: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, borderRadius: "var(--radius)", background: "var(--badge-bg)", color: "var(--blue)", flexShrink: 0 },
  kpiValue: { fontSize: 26, fontWeight: 700, color: "var(--text)", lineHeight: 1.1, fontVariantNumeric: "tabular-nums" },
  kpiLabel: { fontSize: 13, color: "var(--text-muted)", marginTop: 2 },
  actions: { display: "flex", flexWrap: "wrap", gap: 12, marginTop: 28 },
  action: { display: "inline-flex", alignItems: "center", gap: 8, background: "var(--bg-panel)", border: "1px solid var(--border)", borderRadius: "var(--radius)", color: "var(--text)", textDecoration: "none", padding: "10px 16px", fontSize: 14, fontWeight: 600 },
};
